import React, {Suspense, useEffect} from "react";
import {Card, Col, Form, InputGroup, Row, Spinner} from "react-bootstrap";
import {AiOutlineClose} from "react-icons/ai";
import {useAppDispatch, useDimensions} from "../../../redux/stateHooks";
import {useProductDetailsQuery} from "../../../hooks/useProductDetailsQuery";
import {usePriceQuery} from "../../../hooks/usePriceQuery";
import {useDimensionChange} from "../../../hooks/useDimensionChange";
import {
    setDimensions,
    setPendingDimensions
} from "../../../redux/slices/addToCart";

export const DynamicDimensions = React.memo(() => {
    const {width, height} = useDimensions();
    const {
        model: {
            dynamicDimensions: {
                isDynamic,
                minWidth,
                maxWidth,
                minHeight,
                maxHeight
            }
        }
    } = useProductDetailsQuery();

    const dispatch = useAppDispatch();

    // Initialize dimensions to minimum values if dynamic dimensions are enabled
    useEffect(() => {
        // Set dimensions to minimum values if dynamic dimensions are enabled else set dimensions to undefined
        dispatch(setDimensions({
            width: isDynamic ? minWidth : undefined,
            height: isDynamic ? minHeight : undefined
        }));
    }, [isDynamic, minWidth, minHeight]);

    // If the product is not dynamic or the dimensions are not set, do not render the component
    if (!isDynamic || !minWidth || !maxWidth || !minHeight || !maxHeight) return <></>;

    return (
        <Card id="dynamicDimensions" body className="mb-3 mt-0">
            <div className="me-2 d-block mb-1">
                <div>
                    Width (in) x Height (in):{' '}
                    <Suspense fallback={<Spinner animation="border" size="sm" variant="secondary"/>}>
                        <Details/>
                    </Suspense>
                </div>
            </div>
            <Row className="gx-0 justify-content-between d-flex align-items-center">
                <Dimension
                    dimension='width'
                    min={minWidth}
                    max={maxWidth}
                    value={width ?? minWidth}
                />
                <Col xs={1} className="text-center d-none d-sm-block d-md-none d-lg-block">
                    <AiOutlineClose size={18} className="mt-2" aria-label="by"/>
                </Col>
                <Dimension
                    dimension='height'
                    min={minHeight}
                    max={maxHeight}
                    value={height ?? minHeight}
                />
            </Row>
        </Card>
    );
});

export interface IDimension {
    dimension: 'width' | 'height';
    min: number;
    max: number;
    value: number;
}

const Dimension = React.memo((props: IDimension) => {
    const {
        dimension,
        values: {local},
        status: {invalid},
        range: {min, max},
        onChange
    } = useDimensionChange({...props})

 
    return (
        <Col xs={12} sm={5} md={12} lg={5}>
            <Form.Label htmlFor={`dimension-${dimension}`} className="dimension-label mb-0">{dimension}</Form.Label>
            <InputGroup>
                <Form.Control
                    id={`dimension-${dimension}`}
                    type="number"
                    step="0.1"
                    min={min}
                    max={max}
                    value={local}
                    onChange={onChange}
                    placeholder={`${dimension} (in)`}
                    aria-label={`${dimension} in inches`}
                    aria-describedby={`${dimension}-inches-label`}
                    isInvalid={invalid}
                />
                <InputGroup.Text id={`${dimension}-inches-label`}>inches</InputGroup.Text>
            </InputGroup>
            <small className="text-muted mt-1 ms-1">({min} to {max} inches)</small>
            {invalid && (
                <small className="text-danger d-block ms-1 dont-break-out">
                    Please enter a number between {min} and {max}
                </small>
            )}
        </Col>
    )
})

const Details = () => {
    const {error, model: {formatted: {currentPrice, width, height}}} = usePriceQuery();

    if (error) return <></>;

    return (
        <>
            <span className="fw-bold">{width} x {height}{' '}</span>
            <span>({currentPrice})</span>
        </>
    );
}